// iot-admin/client/src/components/ActionsDropdown.js
// Reusable actions dropdown button for device/entity pages

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ActionsDropdown.css';

const ActionsDropdown = ({ actions, disabled = false, label = 'Actions' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleActionClick = (action) => {
    setIsOpen(false);
    action.onClick?.();
  };

  return (
    <div className="actions-dropdown" ref={dropdownRef}>
      <button
        className="actions-dropdown__trigger btn btn-secondary"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {label} <span className="actions-dropdown__caret">▼</span>
      </button>

      {isOpen && (
        <div className="actions-dropdown__menu" role="menu">
          {actions.map((action, i) => {
            if (action.divider) {
              return <div key={i} className="actions-dropdown__divider" />;
            }
            return (
              <button
                key={i}
                className={`actions-dropdown__item${action.danger ? ' actions-dropdown__item--danger' : ''}`}
                onClick={() => handleActionClick(action)}
                disabled={action.disabled}
                role="menuitem"
              >
                {action.icon && (
                  <span className="actions-dropdown__item-icon">{action.icon}</span>
                )}
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

ActionsDropdown.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
        danger: PropTypes.bool,
      }),
      PropTypes.shape({ divider: PropTypes.bool }),
    ]),
  ).isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

export default ActionsDropdown;
